import { Co2MaterialLibrary, Co2MaterialLibraryScope } from "@api/apiClient";
import i18n from "@i18n/i18n";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Box, Checkbox, Chip, ListItem } from "@mui/material";
import TextField from "@mui/material/TextField";
import { observer } from "mobx-react";

export interface MaterialLibrarySelectProps {
  disabled?: boolean;
  options?: Co2MaterialLibrary[];
  value?: Co2MaterialLibrary[];
  showScopeIndicator?: boolean;
  onChange?: (event: any, newSelections: Co2MaterialLibrary[]) => void;
}

const isApplicationMaterialLibrary = (library: Co2MaterialLibrary): boolean =>
  !library.scope || library.scope === Co2MaterialLibraryScope.Application;

const getScopeLabel = (library: Co2MaterialLibrary): string =>
  isApplicationMaterialLibrary(library)
    ? i18n.t("material-library-select.scope.application", { defaultValue: "Application" })
    : i18n.t("material-library-select.scope.project", { defaultValue: "Project" });

const scopeChipSx = {
  fontSize: "0.75rem",
  height: "24px",
  backgroundColor: "rgba(25, 118, 210, 0.08)",
  borderColor: (theme: { palette: { custom?: { borderColor?: string } } }) => theme.palette.custom?.borderColor,
};

const MaterialLibraryScopeChip = ({ library }: { library: Co2MaterialLibrary }) => (
  <Chip label={getScopeLabel(library)} size="small" variant="outlined" sx={scopeChipSx} />
);

const MaterialLibrarySelect = observer((props: MaterialLibrarySelectProps) => {
  return (
    <Autocomplete
      limitTags={2}
      multiple
      id="material-library-select"
      options={props.options || []}
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.co2MaterialLibraryId === value.co2MaterialLibraryId}
      getOptionLabel={(option: Co2MaterialLibrary) => option.name ?? ""}
      renderOption={(renderProps: any, option: Co2MaterialLibrary, { selected }: { selected: boolean }) => {
        const { key, ...rest } = renderProps;
        return (
          <ListItem
            key={option.co2MaterialLibraryId ?? key}
            {...rest}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 2, py: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
              <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={selected} />
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {option.name}
              </Box>
            </Box>
            {props.showScopeIndicator && <MaterialLibraryScopeChip library={option} />}
          </ListItem>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip {...tagProps} key={option.co2MaterialLibraryId ?? key} label={option.name} size="small" />;
        })
      }
      onChange={(event: any, newSelections: Co2MaterialLibrary[]) => {
        if (props.onChange) {
          props.onChange(event, newSelections);
        }
      }}
      value={props.value || []}
      style={{ width: 500, marginTop: 1 }}
      renderInput={(params: any) => {
        const { key, ...rest } = params;
        return <TextField key={key} {...rest} mt={1} label={i18n.t("material-library-select.label")} placeholder={i18n.t("material-library-select.placeholder")} />;
      }}
      disabled={props.disabled}
    />
  );
});

export default MaterialLibrarySelect;
